import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Container, Button, Card, Row, Col, ListGroup, Table, Spinner, Alert, Form, Modal, Badge, InputGroup } from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Event, EventEquipmentItem, EventConsumableItem, EventFile, Category, FullEquipment } from '../interfaces';
import MessageModal from './MessageModal';
import ConfirmationModal from './ConfirmationModal';
import EventForm from './EventForm';
import DefectReportModal from './DefectReportModal';
import EventEndChoiceModal from './EventEndChoiceModal';
import EquipmentDetailModal from './EquipmentDetailModal';
import moment from 'moment';
import _ from 'lodash';
import 'moment/locale/he';
import { useAuth } from '../contexts/AuthContext';
import './print-styles.css';
// אייקונים
import { 
  faEdit, 
  faTrash, 
  faCheck, 
  faDownload, 
  faEye,
  faFile,
  faCog,
  faCalendarAlt,
  faArrowLeft,
  faCopy,
  faExclamationTriangle,
  faTruck,
  faUser,
  faLocationArrow,
  faClock,
  faInfoCircle,
  faPlus,
  faUpload,
  faSave,
  faTimes,
  faPrint,
  faBoxOpen,
  faSearch,
  faFilter
} from '@fortawesome/free-solid-svg-icons';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { API_BASE_URL, buildApiUrl } from '../config/api';

const formatCasesLabel = (quantity: number, caseQty?: number) => {
  const cq = Number(caseQty || 1);
  if (!Number.isFinite(quantity) || quantity <= 0) return null;
  if (!Number.isFinite(cq) || cq <= 1) return null;

  const rawCases = quantity / cq;
  const isInt = Number.isInteger(rawCases);
  const casesText = isInt ? String(rawCases) : rawCases.toFixed(2);
  const noun = rawCases === 1 ? 'קייס' : 'קייסים';
  return `${casesText} ${noun}`;
};

// סגנונות CSS מותאמים
const rentedItemStyles = `
  .print-event-button {
    transition: background-color 0.2s ease, color 0.2s ease, border-color 0.2s ease;
  }

  [data-bs-theme="dark"] .print-event-button,
  body.dark-mode .print-event-button {
    color: #ffffff !important;
    border-color: #ffffff !important;
    background-color: rgba(255, 255, 255, 0.16) !important;
  }

  [data-bs-theme="dark"] .print-event-button:hover,
  body.dark-mode .print-event-button:hover {
    color: #212529 !important;
    background-color: #ffffff !important;
    border-color: #ffffff !important;
  }

  .rented-item-details {
    font-size: 0.85em;
    line-height: 1.3;
  }
  
  .rented-item-details div {
    margin-bottom: 2px;
  }
  
  .rented-item-details div:last-child {
    margin-bottom: 0;
  }
  
  .table-info {
    background-color: #d1ecf1 !important;
  }
  
  .table-info:hover {
    background-color: #bee5eb !important;
  }
  
  .badge.bg-info {
    font-size: 0.7em;
    padding: 0.25em 0.5em;
  }
  
  .equipment-summary-card {
    background-color: #f8f9fa;
    border: 1px solid #dee2e6;
  }
  
  .equipment-summary-item {
    text-align: center;
    padding: 15px;
    border-radius: 8px;
    background-color: white;
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
    height: 100%;
  }
  
  .equipment-summary-item h6 {
    color: #495057;
    margin-bottom: 10px;
    font-weight: 600;
  }
  
  .summary-count {
    font-size: 1.5em;
    font-weight: bold;
    color: #007bff;
    margin-bottom: 5px;
  }
  
  .summary-total {
    font-size: 1.1em;
    color: #6c757d;
    margin-bottom: 8px;
  }
  
  .rented-summary-details {
    border-top: 1px solid #dee2e6;
    padding-top: 8px;
  }
  
  .suppliers-list {
    margin-top: 5px;
    font-style: italic;
  }
  
  .suppliers-list small {
    color: #6c757d;
    line-height: 1.2;
  }
  
  .rental-dates-summary {
    margin-top: 5px;
    font-style: italic;
    color: #6c757d;
  }
  
  .rental-dates-summary small {
    display: block;
    margin-bottom: 2px;
  }
  
  @media print {
    .rented-item-details {
      font-size: 0.8em;
    }
    
    .badge {
      border: 1px solid #000;
      color: #000 !important;
      background-color: transparent !important;
    }
    
    .equipment-summary-card {
      background-color: white !important;
      border: 1px solid #000 !important;
    }
    
    .equipment-summary-item {
      box-shadow: none !important;
      border: 1px solid #000 !important;
    }
  }
`;

const groupEventItems = (items: (EventEquipmentItem | EventConsumableItem)[], sortedCategories: string[]) => {
  const grouped: { [category: string]: { [subcategory: string]: (EventEquipmentItem | EventConsumableItem)[] } } = {};
  const categoriesWithRented = [...sortedCategories, 'מתכלים'];

  items.forEach(item => {
    let category = '';
    let subcategory = '';

    if ('equipment_name' in item) {
      if (item.is_rented_item) {
        // הציוד המושכר יוצג לפי הקטגוריה והתת-קטגוריה שלו
        category = item.rented_item_category || 'כללי';
        subcategory = item.rented_item_subcategory || 'כללי';
      } else {
        category = item.category_name || 'כללי';
        subcategory = item.subcategory_name || 'כללי';
      }
    } else if ('consumable_name' in item) {
      category = 'מתכלים';
      subcategory = 'כללי';
    } else {
      category = 'אחר';
      subcategory = 'אחר';
    }

    if (!grouped[category]) grouped[category] = {};
    if (!grouped[category][subcategory]) grouped[category][subcategory] = [];
    grouped[category][subcategory].push(item);
  });

  const sortedGrouped: { [category: string]: { [subcategory: string]: (EventEquipmentItem | EventConsumableItem)[] } } = {};
  categoriesWithRented.forEach(cat => {
    if (grouped[cat]) {
      sortedGrouped[cat] = grouped[cat];
    }
  });
  if (grouped['מתכלים']) {
    sortedGrouped['מתכלים'] = grouped['מתכלים'];
  }
    
  Object.keys(grouped).forEach(cat => {
      if (!categoriesWithRented.includes(cat)) {
          sortedGrouped[cat] = grouped[cat];
      }
  });

  return sortedGrouped;
};

interface EventDetailsPageProps {
    onBackToList: () => void;
    onBackToCalendar: () => void;
    onManageEquipment: (eventId: number) => void;
    onPrepareEvent: (eventId: number) => void;
}

const STATUS_LABELS: { [key: string]: string } = {
    'planned': 'מתוכנן',
    'preparation': 'בהכנה',
    'active': 'פעיל/בחוץ',
    'completed': 'הסתיים',
    'cancelled': 'בוטל',
    'no_equipment_list': 'ללא רשימת ציוד',
};

const EventDetailsPage: React.FC<EventDetailsPageProps> = ({ onBackToList, onBackToCalendar, onManageEquipment, onPrepareEvent }) => {
  const { user } = useAuth();
    const isTechnician = user?.role?.name === 'TECHNICIAN';
    const canViewNoteAuthor = user?.role?.name === 'ADMIN';
    const { eventId } = useParams<{ eventId: string }>();
    const navigate = useNavigate();
    const eventIdNumber = parseInt(eventId || '0', 10);
    
    const [event, setEvent] = useState<Event | null>(null);
    const [eventEquipment, setEventEquipment] = useState<EventEquipmentItem[]>([]);
    const [eventConsumables, setEventConsumables] = useState<EventConsumableItem[]>([]);
    const [eventFiles, setEventFiles] = useState<EventFile[]>([]);
    const [equipmentAvailability, setEquipmentAvailability] = useState<any>(null);
    const [allCategories, setAllCategories] = useState<Category[]>([]);
    const [allInventoryEquipment, setAllInventoryEquipment] = useState<FullEquipment[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    
    // חיפוש ציוד
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [selectedCategory, setSelectedCategory] = useState<string>('');
    const [selectedSubcategory, setSelectedSubcategory] = useState<string>('');
    const [showSearchFilters, setShowSearchFilters] = useState<boolean>(false);
    
    const [showMessageModal, setShowMessageModal] = useState<boolean>(false);
    const [messageModalTitle, setMessageModalTitle] = useState<string>('');
    const [messageModalContent, setMessageModalContent] = useState<string>('');
    const [messageModalVariant, setMessageModalVariant] = useState<'success' | 'danger' | 'info' | 'warning'>('info');

    const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false);
    const [confirmModalMessage, setConfirmModalMessage] = useState<string>('');
    const [confirmAction, setConfirmAction] = useState<(() => void) | null>(null);
    const [confirmModalTitle, setConfirmModalTitle] = useState<string>('');

      // הצעה לעריכת ציוד
  const [showEquipmentEditSuggestion, setShowEquipmentEditSuggestion] = useState<boolean>(false);
  const [equipmentNeedsReview, setEquipmentNeedsReview] = useState<any[]>([]);
  
  // הצעה לעריכת ציוד לאחר סיום אירוע
  const [showPostCompletionEditSuggestion, setShowPostCompletionEditSuggestion] = useState<boolean>(false);
  const [hadDefectsReported, setHadDefectsReported] = useState<boolean>(false);

      const [showDefectModal, setShowDefectModal] = useState<boolean>(false);
  const [defectDescription, setDefectDescription] = useState<string>('');
  const [isFinishingEvent, setIsFinishingEvent] = useState<boolean>(false);
  
  // תיקון 19: מצב לדיווח תקלות מפורט
  const [defectiveItems, setDefectiveItems] = useState<{id: number, quantity: number, description: string}[]>([]);
  const [showDefectReportModal, setShowDefectReportModal] = useState<boolean>(false);
  const [showEventEndChoiceModal, setShowEventEndChoiceModal] = useState<boolean>(false);

    const [showFileUploadModal, setShowFileUploadModal] = useState<boolean>(false);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [fileDescription, setFileDescription] = useState<string>('');
    const [uploadingFile, setUploadingFile] = useState<boolean>(false);
    const [fileUrl, setFileUrl] = useState<string>('');
    const [selectedFileSize, setSelectedFileSize] = useState<string>('');

  const [showEventFormModal, setShowEventFormModal] = useState<boolean>(false);
  
  // Modal לבחירת ציוד בשכפול
  // משתנים למודל בחירת ציוד בשכפול - הוסרו כי האירוע תמיד מועתק עם כל הציוד

  const [subEvents, setSubEvents] = useState<Event[]>([]);
  
  // Modal לפרטי ציוד
  const [showEquipmentDetailModal, setShowEquipmentDetailModal] = useState<boolean>(false);
  const [selectedEquipmentForDetails, setSelectedEquipmentForDetails] = useState<FullEquipment | null>(null);

    const sortedCategories = useMemo(() => allCategories.sort((a, b) => a.category_name.localeCompare(b.category_name)).map(c => c.category_name), [allCategories]);
    const groupedEventItems = useMemo(() => groupEventItems([...eventEquipment, ...eventConsumables], sortedCategories), [eventEquipment, eventConsumables, sortedCategories]);

    // פונקציה לסינון הציוד לפי חיפוש
    const filteredEventItems = useMemo(() => {
        let filtered = [...eventEquipment, ...eventConsumables];
        
        // סינון לפי טקסט חיפוש
        if (searchTerm) {
            filtered = filtered.filter(item => {
                if ('equipment_name' in item) {
                    return (item.equipment_name || item.rented_item_name || '').toLowerCase().includes(searchTerm.toLowerCase());
                } else if ('consumable_name' in item) {
                    return (item.consumable_name || '').toLowerCase().includes(searchTerm.toLowerCase());
                }
                return false;
            });
        }
        
        // סינון לפי קטגוריה
        if (selectedCategory) {
            filtered = filtered.filter(item => {
                if ('equipment_name' in item) {
                    if (item.is_rented_item) {
                        return (item.rented_item_category || '') === selectedCategory;
                    } else {
                        return (item.category_name || '') === selectedCategory;
                    }
                } else if ('consumable_name' in item) {
                    return selectedCategory === 'מתכלים';
                }
                return false;
            });
        }
        
        // סינון לפי תת-קטגוריה
        if (selectedSubcategory) {
            filtered = filtered.filter(item => {
                if ('equipment_name' in item) {
                    if (item.is_rented_item) {
                        return (item.rented_item_subcategory || '') === selectedSubcategory;
                    } else {
                        return (item.subcategory_name || '') === selectedSubcategory;
                    }
                } else if ('consumable_name' in item) {
                    return selectedSubcategory === 'כללי';
                }
                return false;
            });
        }
        
        return filtered;
    }, [eventEquipment, eventConsumables, searchTerm, selectedCategory, selectedSubcategory]);

    // קבוצת הציוד המסונן
    const filteredGroupedEventItems = useMemo(() => groupEventItems(filteredEventItems, sortedCategories), [filteredEventItems, sortedCategories]);

    // קבלת תת-קטגוריות לפי קטגוריה נבחרת
    const availableSubcategories = useMemo(() => {
        if (!selectedCategory) return [];
        
        const subcategories = new Set<string>();
        [...eventEquipment, ...eventConsumables].forEach(item => {
            if ('equipment_name' in item) {
                if (item.is_rented_item) {
                    if ((item.rented_item_category || '') === selectedCategory) {
                        subcategories.add(item.rented_item_subcategory || 'כללי');
                    }
                } else {
                    if ((item.category_name || '') === selectedCategory) {
                        subcategories.add(item.subcategory_name || 'כללי');
                    }
                }
            } else if ('consumable_name' in item && selectedCategory === 'מתכלים') {
                subcategories.add('כללי');
            }
        });
        
        return Array.from(subcategories).sort();
    }, [eventEquipment, eventConsumables, selectedCategory]);

    // ניקוי סינון
    const clearFilters = () => {
        setSearchTerm('');
        setSelectedCategory('');
        setSelectedSubcategory('');
    };

    const showMessage = (title: string, message: string, variant: 'success' | 'danger' | 'info' | 'warning' = 'info') => {
        setMessageModalTitle(title);
        setMessageModalContent(message);
        setMessageModalVariant(variant);
        setShowMessageModal(true);
    };

    const fetchEventData = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const [eventRes, equipmentRes, consumablesRes, filesRes, allEventsRes, categoriesRes, inventoryRes, availabilityRes] = await Promise.all([
                axios.get(buildApiUrl(`events/events/${eventIdNumber}/`)),
                axios.get(buildApiUrl(`events/event-equipment/?event=${eventIdNumber}`)),
                axios.get(buildApiUrl(`events/event-consumables/?event=${eventIdNumber}`)),
                axios.get(buildApiUrl(`events/event-files/?event=${eventIdNumber}`)),
                axios.get(buildApiUrl('events/events/')),
                axios.get(buildApiUrl('settings/categories/')),
                axios.get(buildApiUrl('inventory/equipment/')),
                axios.get(buildApiUrl(`events/events/${eventIdNumber}/get-equipment-availability-status/`))
            ]);
            setEvent(eventRes.data);
            setEventEquipment(equipmentRes.data);
            setEventConsumables(consumablesRes.data);
            setEventFiles(filesRes.data);
            setEquipmentAvailability(availabilityRes.data);
            // Django REST Framework מחזיר את התוצאות ב-results
            const allEventsData = allEventsRes.data.results || allEventsRes.data;
            setSubEvents(Array.isArray(allEventsData) ? allEventsData.filter((e: Event) => e.parent_event === eventIdNumber) : []);
            setAllCategories(categoriesRes.data);
            setAllInventoryEquipment(inventoryRes.data);
        } catch (err) {
            setError("לא ניתן לטעון את פרטי האירוע. וודא ששרת Django פועל.");
        } finally {
            setLoading(false);
        }
    }, [eventIdNumber]);

    useEffect(() => {
        fetchEventData();
    }, [fetchEventData]);

    useEffect(() => {
        console.log('showDefectReportModal changed to:', showDefectReportModal);
    }, [showDefectReportModal]);
    
    const handleEndEventClick = () => {
        console.log('handleEndEventClick called');
        console.log('event:', event);
        console.log('event status:', event?.status);
        
        if (!event || event.status === 'completed' || event.status === 'cancelled') {
            console.log('Event is null or completed/cancelled, returning');
            return;
        }

        // בדיקה שלא ניתן לסיים אירוע לפני התאריך שלו
        const now = new Date();
        const eventDate = new Date(event?.start_date);
        
        if (now < eventDate) {
            const daysUntilEvent = Math.ceil((eventDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
            showMessage('שגיאה', `לא ניתן לסיים אירוע לפני התאריך שלו. האירוע מתוכנן ל-${eventDate.toLocaleDateString('he-IL')} (בעוד ${daysUntilEvent} ימים). ניתן לסמן כבוטל או למחוק אותו.`, 'danger');
            return;
        }

        console.log('Opening event end choice modal');
        // פתיחת מודל בחירה דו-שלבי
        setShowEventEndChoiceModal(true);
    };

    const handleEndWithoutDefects = async (completionNotes: string = '') => {
        // בדיקה נוספת שלא ניתן לסיים אירוע לפני התאריך שלו
        if (!event?.start_date) return;
        
        const now = new Date();
        const eventDate = new Date(event.start_date);
        
        if (now < eventDate) {
            showMessage('שגיאה', 'לא ניתן לסיים אירוע לפני התאריך שלו. ניתן לסמן כבוטל או למחוק אותו', 'danger');
            setShowEventEndChoiceModal(false);
            return;
        }

        setShowEventEndChoiceModal(false);
        setIsFinishingEvent(true);
        try {
            await axios.post(buildApiUrl(`events/events/${eventIdNumber}/end_event/`), { completion_notes: completionNotes });
            
            // סמן שאין דיווח תקלות
            setHadDefectsReported(false);
            
            // הצגת מודל הצעה לעריכת ציוד
            setShowPostCompletionEditSuggestion(true);
            
            fetchEventData();
        } catch (err) {
            console.error("שגיאה בסיום אירוע:", err);
            showMessage('שגיאה', 'שגיאה בסיום האירוע. אנא נסה שוב.', 'danger');
        } finally {
            setIsFinishingEvent(false);
        }
    };

    const handleEndWithDefects = () => {
        setShowEventEndChoiceModal(false);
        setShowDefectReportModal(true);
    };

    const handleDefectReportSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        
        // בדיקה נוספת שלא ניתן לסיים אירוע לפני התאריך שלו
        if (!event?.start_date) return;
        
        const now = new Date();
        const eventDate = new Date(event.start_date);
        
        if (now < eventDate) {
            showMessage('שגיאה', 'לא ניתן לסיים אירוע לפני התאריך שלו. ניתן לסמן כבוטל או למחוק אותו', 'danger');
            setShowDefectModal(false);
            return;
        }
        
        setIsFinishingEvent(true);
        try {
            await axios.post(buildApiUrl(`events/events/${eventIdNumber}/end_event/`), { defect_description: defectDescription });
            const endedNameWithDefect = event?.event_name ? `"${event.event_name}"` : 'האירוע';
            showMessage('הצלחה', `${endedNameWithDefect} הסתיים בהצלחה! הציוד הוחזר למחסן.`, 'success');
            setShowDefectModal(false);
            setDefectDescription('');
            fetchEventData();
        } catch (err) {
            console.error("שגיאה בסיום אירוע:", err);
            showMessage('שגיאה', 'שגיאה בסיום האירוע. אנא נסה שוב.', 'danger');
        } finally {
            setIsFinishingEvent(false);
        }
    };

    // פונקציה לטיפול בדיווח תקלות מפורט
    const canPrepareEquipment = (event: Event) => {
        if (!event.start_date) return true;
        
        const now = new Date();
        const eventDate = new Date(event.start_date);
        const oneWeekBefore = new Date(eventDate.getTime() - 7 * 24 * 60 * 60 * 1000);
        
        return now >= oneWeekBefore;
    };

    const handleDetailedDefectReport = async (defectData: any) => {
        // בדיקה נוספת שלא ניתן לסיים אירוע לפני התאריך שלו
        if (!event?.start_date) return;
        
        const now = new Date();
        const eventDate = new Date(event.start_date);
        
        if (now < eventDate) {
            showMessage('שגיאה', 'לא ניתן לסיים אירוע לפני התאריך שלו. ניתן לסמן כבוטל או למחוק אותו', 'danger');
            setShowDefectReportModal(false);
            return;
        }
        
        setIsFinishingEvent(true);
        try {
            const response = await axios.post(buildApiUrl(`events/events/${eventIdNumber}/end_event/`), defectData);
            
            /* סגירת מודל דיווח התקלות — אחרי הודעת הצלחה ב-DefectReportModal */
            
            // סמן שיש דיווח תקלות
            setHadDefectsReported(true);
            
            // הצגת מודל הצעה לעריכת ציוד
            setShowPostCompletionEditSuggestion(true);
            
            fetchEventData();
        } catch (err) {
            console.error("שגיאה בסיום אירוע עם דיווח תקלות:", err);
            showMessage('שגיאה', 'שגיאה בסיום האירוע. אנא נסה שוב.', 'danger');
        } finally {
            setIsFinishingEvent(false);
        }
    };

    const handleSimpleEndEvent = async () => {
        // בדיקה נוספת שלא ניתן לסיים אירוע לפני התאריך שלו
        if (!event?.start_date) return;
        
        const now = new Date();
        const eventDate = new Date(event.start_date);
        
        if (now < eventDate) {
            showMessage('שגיאה', 'לא ניתן לסיים אירוע לפני התאריך שלו. ניתן לסמן כבוטל או למחוק אותו', 'danger');
            setShowDefectReportModal(false);
            return;
        }
        
        setIsFinishingEvent(true);
        try {
            const response = await axios.post(buildApiUrl(`events/events/${eventIdNumber}/end_event/`), {});
            const endedNameSimple = event?.event_name ? `"${event.event_name}"` : 'האירוע';
            
            // בדיקה אם יש ציוד שצריך עריכה
            const equipmentNeedsReview = response.data.equipment_needs_review || [];
            
            if (equipmentNeedsReview.length > 0) {
                // הצגת הצעה לעריכת ציוד
                setShowEquipmentEditSuggestion(true);
                setEquipmentNeedsReview(equipmentNeedsReview);
            } else {
                showMessage('הצלחה', `${endedNameSimple} הסתיים בהצלחה! הציוד הוחזר למחסן.`, 'success');
            }
            
            setShowDefectReportModal(false);
            fetchEventData();
        } catch (err) {
            console.error("שגיאה בסיום אירוע:", err);
            showMessage('שגיאה', 'שגיאה בסיום האירוע. אנא נסה שוב.', 'danger');
        } finally {
            setIsFinishingEvent(false);
        }
    };

    const handleDuplicateClick = async () => {
        setConfirmModalTitle('אישור שכפול');
        setConfirmModalMessage('האם אתה בטוח שברצונך לשכפל אירוע זה?');
        setConfirmAction(() => async () => {
            try {
                // סגירת מודל האישור מיד כשהמשתמש מאשר
                setShowConfirmModal(false);
                const response = await axios.post(buildApiUrl(`events/events/${eventIdNumber}/duplicate/`));
                const newEvent = response.data;
                
                // הצגת הודעת הצלחה עם פרטים על זמינות הציוד
                const unavailableCount = newEvent.equipment_availability?.unavailable_equipment?.length || 0;
                const availableCount = newEvent.equipment_availability?.available_equipment?.length || 0;
                
                let message = `האירוע שוכפל בהצלחה עם כל הציוד!\n\n`;
                message += `ציוד זמין: ${availableCount} פריטים\n`;
                
                if (unavailableCount > 0) {
                    message += `ציוד לא זמין: ${unavailableCount} פריטים\n\n`;
                    message += `הציוד הלא זמין:\n`;
                    newEvent.equipment_availability.unavailable_equipment.forEach((item: any) => {
                        message += `• ${item.equipment_name}: ${item.unavailable_quantity} יחידות לא זמינות (${item.reason})\n`;
                    });
                } else {
                    message += `כל הציוד זמין!`;
                }
                
                // הוספת הערות לאחר סיום אם קיימות
                if (newEvent.post_completion_notes_copied) {
                    message += `\n\n━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n\n`;
                    message += `⚠️ הערות שהוגדרו לאחר סיום האירוע המקורי הועברו להערות הכלליות של האירוע המשוכפל:`;
                    message += `\n\n"${newEvent.post_completion_notes_copied}"`;
                }
                
                showMessage('הצלחה', message, 'success');
                
                // פתיחת טופס העריכה לאחר השכפול
                setEvent(newEvent);
                setShowEventFormModal(true);
            } catch (err) {
                console.error("שגיאה בשכפול אירוע:", err);
                showMessage('שגיאה', 'שגיאה בשכפול האירוע. אנא נסה שוב.', 'danger');
            }
        });
        setShowConfirmModal(true);
    };

    // פונקציות לטיפול בבחירת ציוד בשכפול - הוסרו כי האירוע תמיד מועתק עם כל הציוד

    const handleDeleteClick = () => {
        setConfirmModalTitle('אישור מחיקה');
        setConfirmModalMessage('האם אתה בטוח שברצונך למחוק אירוע זה? פעולה זו בלתי הפיכה.');
        setConfirmAction(() => async () => {
            try {
                await axios.delete(buildApiUrl(`events/events/${eventIdNumber}/`));
                const deletedName = event?.event_name ? `"${event.event_name}"` : '';
                showMessage('הצלחה', deletedName ? `האירוע ${deletedName} נמחק בהצלחה!` : 'האירוע נמחק בהצלחה!', 'success');
                onBackToList();
            } catch (err) {
                console.error("שגיאה במחיקת אירוע:", err);
                showMessage('שגיאה', 'שגיאה במחיקת האירוע. אנא נסה שוב.', 'danger');
            }
        });
        setShowConfirmModal(true);
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];
            setSelectedFile(file);
            setFileUrl('');
            // חישוב גודל הקובץ במגה בייט
            const fileSizeMB = (file.size / (1024 * 1024)).toFixed(2);
            setSelectedFileSize(fileSizeMB);
        } else {
            setSelectedFile(null);
            setSelectedFileSize('');
        }
    };

    const handleUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFileUrl(e.target.value);
        setSelectedFile(null);
        setSelectedFileSize('');
    };

    const handleFileUpload = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!selectedFile && !fileUrl.trim()) {
            showMessage('שגיאה', 'אנא בחר קובץ או הזן קישור להעלאה.', 'danger');
            return;
        }
        if (fileUrl.trim() && !fileDescription.trim()) {
            showMessage('שגיאה', 'כשמוסיפים קישור — חובה למלא תיאור. כתוב תיאור בשדה למטה.', 'danger');
            return;
        }
        
        // בדיקת גודל קובץ מקסימלי - 20 מגה בייט
        if (selectedFile) {
            const maxFileSize = 20 * 1024 * 1024; // 20MB in bytes
            if (selectedFile.size > maxFileSize) {
                const fileSizeMB = (selectedFile.size / (1024 * 1024)).toFixed(2);
                showMessage('שגיאה', `הקובץ גדול מדי. הגודל המקסימלי המותר הוא 20 מגה בייט. גודל הקובץ הנוכחי: ${fileSizeMB} מגה בייט.`, 'danger');
                return;
            }
        }

        setUploadingFile(true);
        const formData = new FormData();
        formData.append('description', fileDescription.trim());

        if (selectedFile) {
            formData.append('file', selectedFile);
        } else if (fileUrl.trim()) {
            formData.append('file_url', fileUrl.trim());
        }

        try {
            await axios.post(buildApiUrl(`events/events/${eventIdNumber}/upload_file/`), formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });
            const uploadedDescriptor = selectedFile?.name ? `הקובץ "${selectedFile.name}"` : (fileUrl ? `הקישור "${fileUrl}"` : 'הקובץ/קישור');
            showMessage('הצלחה', `${uploadedDescriptor} הועלה לאירוע בהצלחה!`, 'success');
            setShowFileUploadModal(false);
            setSelectedFile(null);
            setFileDescription('');
            setFileUrl('');
            setSelectedFileSize('');
            fetchEventData();
        } catch (err) {
            console.error("שגיאה בהעלאת קובץ/קישור:", err);
            let errorMessage = 'שגיאה בהעלאת קובץ/קישור. אנא נסה שוב.';
            
            // טיפול בהודעות שגיאה ספציפיות מהשרת
            if (axios.isAxiosError(err) && err.response && err.response.data) {
                const errorData = err.response.data as Record<string, unknown>;
                if (typeof errorData.error === 'string') {
                    errorMessage = errorData.error;
                } else if (typeof errorData.detail === 'string') {
                    errorMessage = errorData.detail;
                } else if (Array.isArray(errorData.detail) && errorData.detail.length) {
                    errorMessage = String(errorData.detail[0]);
                } else if (typeof errorData === 'string') {
                    errorMessage = errorData;
                }
            }
            
            showMessage('שגיאה', errorMessage, 'danger');
        } finally {
            setUploadingFile(false);
        }
    };

    const handleEditEventDetails = () => {
        setShowEventFormModal(true);
    };
    
    // <--- תיקון 10: פונקציה לפתיחת טופס עריכה מתת-אירוע
    const handleEditSubEventDetails = (subEvent: Event) => {
        setEvent(subEvent);
        setShowEventFormModal(true);
    };

    const handleEventFormSuccess = (updatedEvent: Event) => {
        setShowEventFormModal(false);
        // נעדכן את האירוע המקומי כדי שהפרטים יוצגו מעודכנים
        setEvent(updatedEvent);
        fetchEventData();
        const eventNameForMsg = updatedEvent.event_name ? `"${updatedEvent.event_name}"` : 'האירוע';
        showMessage('הצלחה', `פרטי ${eventNameForMsg} עודכנו בהצלחה!`, 'success');
    };

    const handleEventFormCancel = () => {
        setShowEventFormModal(false);
    };
    
    // תיקון 12: פונקציה לפיצול התיאור
    const parseDescription = (description: string | null) => {
        const defaultSections = {
            amplification: '',
            lighting: '',
            video: '',
            electrical: '',
            general: '',
        };
        if (!description) return defaultSections;

        const sections = description.split('---SPEC_DELIMITER---');
        if (sections.length === 5) {
            return {
                amplification: sections[0],
                lighting: sections[1],
                video: sections[2],
                electrical: sections[3],
                general: sections[4],
            };
        }
        return { ...defaultSections, general: description };
    };

    const parsedDescription = useMemo(() => parseDescription(event?.description || null), [event?.description]);

    const getStatusVariant = (status: string) => {
      const statusChoice = [
        { value: 'planned', label: 'מתוכנן', variant: 'primary' },
        { value: 'preparation', label: 'בהכנה', variant: 'warning' },
        { value: 'active', label: 'פעיל/בחוץ', variant: 'success' },
        { value: 'completed', label: 'הסתיים', variant: 'secondary' },
        { value: 'cancelled', label: 'בוטל', variant: 'danger' },
        { value: 'no_equipment_list', label: 'ללא רשימת ציוד', variant: 'light' },
      ].find(choice => choice.value === status);
      return statusChoice ? statusChoice.variant : 'secondary';
    };

    const handleSubEventClick = (subEventId: number) => {
        navigate(`/event-details/${subEventId}`);
    };

    // פונקציה לטיפול בלחיצה על פריט ציוד
    const handleEquipmentRowClick = (equipmentItem: EventEquipmentItem) => {
        if (equipmentItem.is_rented_item) {
            // עבור ציוד מושכר, נציג הודעה שאין פרטים מלאים
            showMessage('מידע', 'לציוד מושכר אין פרטים מלאים במערכת. פרטי הציוד המושכר מוצגים בטבלה.', 'info');
            return;
        }
        
        // מצא את הציוד המלא במלאי
        const fullEquipment = allInventoryEquipment.find(eq => eq.id === equipmentItem.equipment);
        if (fullEquipment) {
            setSelectedEquipmentForDetails(fullEquipment);
            setShowEquipmentDetailModal(true);
        } else {
            showMessage('שגיאה', 'לא ניתן למצוא פרטי ציוד מלאים.', 'danger');
        }
    };

    const VIEWABLE_EXTENSIONS = ['pdf', 'png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'txt', 'csv', 'mp4', 'webm', 'mp3', 'wav'];

    const getFileExtension = (file: EventFile): string => {
      const source = (file.original_filename || file.file_url || (typeof file.file === 'string' ? file.file : '') || '').toLowerCase();
      if (!source.includes('.')) return '';
      const cleanSource = source.split('?')[0].split('#')[0];
      return cleanSource.substring(cleanSource.lastIndexOf('.') + 1);
    };

    /** יש קישור חיצוני או קובץ שנשמר בשרת (גם אם ה-API לא מחזיר URL בשדה file — למשל GCS) */
    const hasAttachmentActions = (file: EventFile): boolean => {
      const url = file.file_url?.trim();
      if (url) return true;
      if (typeof file.file === 'string' && file.file.trim()) return true;
      if (file.id != null && !url) return true;
      return false;
    };

    const isViewableFile = (file: EventFile): boolean => {
      if (file.file_url?.trim()) return true; // קישור תמיד נפתח בכרטיסיה חדשה
      const extension = getFileExtension(file);
      return VIEWABLE_EXTENSIONS.includes(extension);
    };

    // תיקון 3: לוגיקה נפרדת לתצוגה והורדה
    const handleFileView = async (file: EventFile) => {
      if (file.file_url?.trim()) {
        window.open(file.file_url, '_blank');
      } else if (file.id != null && !file.file_url?.trim()) {
        const url = buildApiUrl(`events/events/${eventIdNumber}/download_file/${file.id}/`);
        try {
            const response = await axios.get(url, { responseType: 'blob' });
            const blob = new Blob([response.data], { type: response.headers['content-type'] });
            const viewUrl = window.URL.createObjectURL(blob);
            window.open(viewUrl, '_blank');
            window.URL.revokeObjectURL(viewUrl);
        } catch (err) {
            console.error("שגיאה בתצוגת קובץ:", err);
            showMessage('שגיאה', 'שגיאה בתצוגת הקובץ. אנא נסה שוב.', 'danger');
        }
      }
    };

    const handleFileDownload = async (file: EventFile) => {
      if (file.file_url?.trim()) {
        // עבור קישורים, ננסה להוריד את הקובץ
        try {
            const response = await axios.get(file.file_url!, { responseType: 'blob' });
            const blob = new Blob([response.data]);
            const downloadUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.setAttribute('download', file.original_filename || `file-${file.id}`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(downloadUrl);
        } catch (err) {
            // אם לא ניתן להוריד, נפתח בחלון חדש
            window.open(file.file_url!, '_blank');
        }
      } else if (file.id != null) {
        const url = buildApiUrl(`events/events/${eventIdNumber}/download_file/${file.id}/`);
        try {
            const response = await axios.get(url, { responseType: 'blob' });
            const blob = new Blob([response.data], { type: response.headers['content-type'] });
            const downloadUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.setAttribute('download', file.original_filename || `file-${file.id}`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(downloadUrl);
        } catch (err) {
            console.error("שגיאה בהורדת קובץ:", err);
            showMessage('שגיאה', 'שגיאה בהורדת הקובץ. אנא נסה שוב.', 'danger');
        }
      }
    };
    
    if (loading) {
        return <Spinner animation="border" />;
    }
    
    if (error) {
        return <Alert variant="danger">{error}</Alert>;
    }
    
    if (!event) {
        return <Alert variant="warning">האירוע המבוקש אינו קיים.</Alert>;
    }

    return (
        <Container className="event-details-page">
            <style>{rentedItemStyles}</style>
            <div className="d-flex justify-content-between mb-4 d-print-none app-sticky-page-toolbar">
                <div>
                    <Button variant="secondary" onClick={onBackToList} className="me-2">
                        <FontAwesomeIcon icon={faArrowLeft} className="me-1" />
                        חזרה לרשימת האירועים
                    </Button>
                    <Button variant="secondary" onClick={onBackToCalendar}>
                        <FontAwesomeIcon icon={faCalendarAlt} className="me-1" />
                        חזרה ללוח שנה
                    </Button>
                </div>
                <div>
                    <Button variant="outline-secondary" onClick={() => window.print()} className="print-event-button">
                        <FontAwesomeIcon icon={faPrint} className="me-1" />
                        הדפס פרטי אירוע
                    </Button>
                </div>
            </div>
            
            <Card className="mb-4 event-details-card">
                <Card.Header className="d-flex align-items-center">
                    {event.color_hex && event.status === 'preparation' && (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginLeft: '15px' }}>
                            <div
                                style={{
                                    width: '30px',
                                    height: '30px',
                                    backgroundColor: event.color_hex,
                                    border: '1px solid #ccc',
                                    borderRadius: '5px',
                                    marginBottom: '4px'
                                }}
                                title={event.color_name}
                            ></div>
                            {event.color_name && <small>{event.color_name}</small>}
                        </div>
                    )}
                    <Card.Title className="mb-0">
                        פרטי אירוע: {event.event_name}
                    </Card.Title>
                </Card.Header>
                <Card.Body>
                    {/* התראה על ציוד לא זמין */}
                    {equipmentAvailability?.equipment_availability?.has_unavailable && (
                        <Alert variant="warning" className="mb-3">
                            <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                            <strong>אזהרה:</strong> חלק מהציוד לא זמין לתאריכי האירוע!
                            <br />
                            <small>
                                {equipmentAvailability.equipment_availability.unavailable_count} פריטים לא זמינים מתוך {equipmentAvailability.equipment_availability.total_equipment} פריטי ציוד.
                                <br />
                                הציוד הלא זמין: {equipmentAvailability.equipment_availability.unavailable_equipment.map((item: any) => item.equipment_name).join(', ')}
                            </small>
                        </Alert>
                    )}
                    
                    <Row>
                        <Col md={6}>
                            <ListGroup variant="flush">
                                <ListGroup.Item><strong>לקוח:</strong> {event.client_name || event.free_text_client || 'אין'}</ListGroup.Item>
                                {event.client_details && event.client_details.contact_person && (
                                    <ListGroup.Item className="ms-3"><strong>איש קשר:</strong> {event.client_details.contact_person}</ListGroup.Item>
                                )}
                                {event.client_details && event.client_details.phone && (
                                    <ListGroup.Item className="ms-3"><strong>טלפון:</strong> {event.client_details.phone}</ListGroup.Item>
                                )}
                                {event.client_details && event.client_details.email && (
                                    <ListGroup.Item className="ms-3"><strong>אימייל:</strong> {event.client_details.email}</ListGroup.Item>
                                )}
                                <ListGroup.Item><strong>תאריך התחלה:</strong> {moment(event.start_date).format('LLL')}</ListGroup.Item>
                                <ListGroup.Item><strong>תאריך סיום:</strong> {moment(event.end_date).format('LLL')}</ListGroup.Item>
                                <ListGroup.Item><strong>מיקום:</strong> {event.location_name || event.free_text_location || 'אין'}</ListGroup.Item>
                                {event.location_details && event.location_details.location_type && (
                                    <ListGroup.Item className="ms-3"><strong>סוג מיקום:</strong> {event.location_details.location_type}</ListGroup.Item>
                                )}
                                {event.location_details && event.location_details.address && (
                                    <ListGroup.Item className="ms-3"><strong>כתובת:</strong> {event.location_details.address}</ListGroup.Item>
                                )}
                                <ListGroup.Item><strong>סטטוס:</strong> {STATUS_LABELS[event.status] || event.status}</ListGroup.Item>
                                {event.is_internal !== undefined && <ListGroup.Item><strong>האם האירוע בפנים או בחוץ?</strong> {event.is_internal ? 'פנימי' : 'חיצוני'}</ListGroup.Item>}
                                {parsedDescription.amplification && <ListGroup.Item><strong>מפרט הגברה:</strong> {parsedDescription.amplification}</ListGroup.Item>}
                                {parsedDescription.lighting && <ListGroup.Item><strong>מפרט תאורה:</strong> {parsedDescription.lighting}</ListGroup.Item>}
                                {parsedDescription.video && <ListGroup.Item><strong>מפרט וידאו:</strong> {parsedDescription.video}</ListGroup.Item>}
                                {parsedDescription.electrical && <ListGroup.Item><strong>מפרט חשמל:</strong> {parsedDescription.electrical}</ListGroup.Item>}
                                {parsedDescription.general && <ListGroup.Item><strong>הערות כלליות:</strong> {parsedDescription.general}</ListGroup.Item>}
                                {event.pre_completion_notes && <ListGroup.Item><strong>הערות לפני סיום:</strong> {event.pre_completion_notes}</ListGroup.Item>}
                                {event.post_completion_notes && <ListGroup.Item><strong style={{color: '#0066cc'}}>הערות לאחר סיום:</strong> <span style={{color: '#0066cc'}}>{event.post_completion_notes}</span></ListGroup.Item>}
                            </ListGroup>
                        </Col>
                        <Col md={6} className="d-print-none">
                            <h5>פעולות</h5>
                            <div className="d-grid gap-2">
                                <Button variant="primary" onClick={() => onManageEquipment(event.id!)} disabled={user?.role?.name === 'TECHNICIAN'} title={user?.role?.name === 'TECHNICIAN' ? 'אין לך הרשאה לניהול ציוד' : ''}>
                                    <FontAwesomeIcon icon={faCog} className="me-1" />
                                     ניהול ציוד
                                </Button>
                                <Button 
                                    variant="secondary" 
                                    onClick={() => onPrepareEvent(event.id!)}
                                    disabled={user?.role?.name === 'TECHNICIAN' || !canPrepareEquipment(event)}
                                    title={user?.role?.name === 'TECHNICIAN' ? 'אין לך הרשאה להכנת/העמסת אירועים' : (!canPrepareEquipment(event) ? 'לא ניתן להכין ציוד לפני השבוע שלפני התאריך' : '')}
                                    style={{ backgroundColor: '#8A2BE2', borderColor: '#8A2BE2' }}
                                >
                                    <FontAwesomeIcon icon={faTruck} className="me-1" />
                                     הכנה / העמסה
                                </Button>
                                <Button variant="warning" onClick={handleEditEventDetails} disabled={user?.role?.name === 'TECHNICIAN'} title={user?.role?.name === 'TECHNICIAN' ? 'אין לך הרשאה לערוך פרטי אירוע' : ''}>
                                    <FontAwesomeIcon icon={faEdit} className="me-1" />
                                     ערוך פרטים
                                </Button>
                                {event.status !== 'completed' && event.status !== 'cancelled' && (
                                    <Button 
                                        variant="success" 
                                        onClick={handleEndEventClick} 
                                        disabled={user?.role?.name === 'TECHNICIAN' || isFinishingEvent || (new Date() < new Date(event.start_date))}
                                        title={user?.role?.name === 'TECHNICIAN' ? 'אין לך הרשאה לסיים אירוע' : (new Date() < new Date(event.start_date) ? 'לא ניתן לסיים אירוע לפני התאריך שלו' : '')}
                                    >
                                        {isFinishingEvent ? <Spinner animation="border" size="sm" /> : <><FontAwesomeIcon icon={faCheck} className="me-1" /> סיים אירוע</>}
                                    </Button>
                                )}
                                <Button variant="danger" onClick={handleDeleteClick} disabled={user?.role?.name === 'TECHNICIAN' || (user?.role?.name !== 'ADMIN' && user?.role?.name !== 'PRODUCTION')} title={user?.role?.name === 'TECHNICIAN' ? 'אין לך הרשאה למחיקת אירוע' : (user?.role?.name !== 'ADMIN' && user?.role?.name !== 'PRODUCTION' ? 'רק מנהלים והרצה יכולים למחוק אירועים' : '')}>
                                    <FontAwesomeIcon icon={faTrash} className="me-1" />
                                     מחק
                                </Button>
                                <Button variant="secondary" onClick={() => handleDuplicateClick()} disabled={user?.role?.name === 'TECHNICIAN' || user?.role?.name === 'TEAM_LEADER'} title={user?.role?.name === 'TECHNICIAN' || user?.role?.name === 'TEAM_LEADER' ? 'אין לך הרשאה לשכפל אירוע' : ''}>
                                    <FontAwesomeIcon icon={faCopy} className="me-1" />
                                     שכפל
                                </Button>
                                <Button variant="outline-primary" onClick={() => setShowFileUploadModal(true)}>
                                    <FontAwesomeIcon icon={faFile} className="me-1" />
                                     הוסף קובץ/קישור
                                </Button>
                            </div>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            {/* סיכום הציוד */}
            {(eventEquipment.length > 0 || eventConsumables.length > 0) && (
                <Card className="mb-4 equipment-summary-card">
                    <Card.Header>סיכום ציוד</Card.Header>
                    <Card.Body>
                        <Row>
                            <Col md={4}>
                                <div className="equipment-summary-item">
                                    <h6>ציוד פנימי</h6>
                                    <div className="summary-count">
                                        {eventEquipment.filter(item => !item.is_rented_item).length} פריטים
                                    </div>
                                    <div className="summary-total">
                                        סה"כ: {eventEquipment.filter(item => !item.is_rented_item).reduce((sum, item) => sum + item.quantity_needed, 0)} יחידות
                                    </div>
                                </div>
                            </Col>
                            <Col md={4}>
                                <div className="equipment-summary-item">
                                    <h6>ציוד מושכר</h6>
                                    <div className="summary-count">
                                        {eventEquipment.filter(item => item.is_rented_item).length} פריטים
                                    </div>
                                    <div className="summary-total">
                                        סה"כ: {eventEquipment.filter(item => item.is_rented_item).reduce((sum, item) => sum + item.quantity_needed, 0)} יחידות
                                    </div>
                                    {eventEquipment.filter(item => item.is_rented_item).length > 0 && (
                                        <div className="rented-summary-details">
                                            <small className="text-muted">
                                                {new Set(eventEquipment.filter(item => item.is_rented_item && item.rental_supplier_name).map(item => item.rental_supplier_name)).size} ספקים שונים
                                            </small>
                                            <div className="suppliers-list">
                                                <small className="text-muted">
                                                    {Array.from(new Set(eventEquipment.filter(item => item.is_rented_item && item.rental_supplier_name).map(item => item.rental_supplier_name))).slice(0, 3).join(', ')}
                                                    {new Set(eventEquipment.filter(item => item.is_rented_item && item.rental_supplier_name).map(item => item.rental_supplier_name)).size > 3 && '...'}
                                                </small>
                                            </div>
                                            {eventEquipment.filter(item => item.is_rented_item && (item.rented_arrival_date || item.rented_departure_date)).length > 0 && (
                                                <div className="rental-dates-summary">
                                                    <small className="text-muted">
                                                        {eventEquipment.filter(item => item.is_rented_item && item.rented_arrival_date).length} פריטים עם תאריכי הגעה
                                                    </small>
                                                    <small className="text-muted">
                                                        {eventEquipment.filter(item => item.is_rented_item && item.rented_departure_date).length} פריטים עם תאריכי עזיבה
                                                    </small>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </Col>
                            <Col md={4}>
                                <div className="equipment-summary-item">
                                    <h6>מתכלים</h6>
                                    <div className="summary-count">
                                        {eventConsumables.length} פריטים
                                    </div>
                                    <div className="summary-total">
                                        סה"כ: {eventConsumables.reduce((sum, item) => sum + item.quantity_used, 0)} יחידות
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    </Card.Body>
                </Card>
            )}

            {subEvents.length > 0 && (
                <Card className="mb-4">
                    <Card.Header>תתי-אירועים</Card.Header>
                    <Card.Body>
                        <Table striped bordered hover responsive size="sm" className="table-to-print">
                            <thead>
                                <tr>
                                    <th>#</th>
                                    <th>שם אירוע</th>
                                    <th>לקוח</th>
                                    <th>תאריך התחלה</th>
                                    <th>תאריך סיום</th>
                                    <th>סטטוס</th>
                                    <th className="d-print-none">פעולות</th>
                                </tr>
                            </thead>
                            <tbody>
                                {subEvents.map(subEvent => (
                                    <tr key={subEvent.id} onClick={() => handleSubEventClick(subEvent.id!)} style={{ cursor: 'pointer' }}>
                                        <td>{subEvent.id}</td>
                                        <td>{subEvent.event_name}</td>
                                        <td>{subEvent.client_name}</td>
                                        <td>{new Date(subEvent.start_date).toLocaleString('he-IL')}</td>
                                        <td>{new Date(subEvent.end_date).toLocaleString('he-IL')}</td>
                                        <td>
                                            {subEvent.status === 'no_equipment_list' ? (
                                                <span className="badge" style={{ backgroundColor: '#f8bbd0', color: '#000' }}>
                                                    {STATUS_LABELS[subEvent.status] || subEvent.status}
                                                </span>
                                            ) : (
                                                <Badge bg={getStatusVariant(subEvent.status)}>
                                                  {STATUS_LABELS[subEvent.status] || subEvent.status}
                                                </Badge>
                                            )}
                                        </td>
                                        <td className="d-print-none">
                                            <Button variant="primary" size="sm" className="me-2" onClick={(e) => { e.stopPropagation(); onManageEquipment(subEvent.id!); }}>
                                              <FontAwesomeIcon icon={faCog} className="me-1" />
                                              ניהול ציוד
                                            </Button>
                                            <Button variant="secondary" size="sm" className="me-2" onClick={(e) => { e.stopPropagation(); onPrepareEvent(subEvent.id!); }} style={{ backgroundColor: '#8A2BE2', borderColor: '#8A2BE2' }}>
                                                <FontAwesomeIcon icon={faTruck} className="me-1" />
                                                הכנה / העמסה
                                            </Button>
                                            <Button variant="warning" size="sm" className="me-2" onClick={(e) => { e.stopPropagation(); handleEditSubEventDetails(subEvent); }}>
                                                <FontAwesomeIcon icon={faEdit} className="me-1" />
                                                ערוך
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </Card.Body>
                </Card>
            )}

            <Card className="mb-4 event-equipment-section">
                <Card.Header>רשימת ציוד לאירוע</Card.Header>
                <Card.Body>
                    {/* התראה על ציוד לא זמין ברשימת הציוד */}
                    {equipmentAvailability?.equipment_availability?.has_unavailable && (
                        <Alert variant="danger" className="mb-3">
                            <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                            <strong>ציוד לא זמין:</strong> הפריטים הבאים לא זמינים לתאריכי האירוע:
                            <ul className="mb-0 mt-2">
                                {equipmentAvailability.equipment_availability.unavailable_equipment.map((item: any, index: number) => (
                                    <li key={index}>
                                        <strong>{item.equipment_name}</strong> ({item.quantity_needed} יחידות) - {item.availability_reason}
                                    </li>
                                ))}
                            </ul>
                        </Alert>
                    )}
                    
                                         <div className="d-print-none search-container">
                         <InputGroup className="mb-3 equipment-search-section">
                             <InputGroup.Text><FontAwesomeIcon icon={faSearch} /></InputGroup.Text>
                             <Form.Control
                                 className="search-input"
                                 placeholder="חפש פריטים..."
                                 value={searchTerm}
                                 onChange={(e) => setSearchTerm(e.target.value)}
                             />
                             <Button 
                                 className="filter-button"
                                 variant="outline-secondary" 
                                 onClick={() => setShowSearchFilters(!showSearchFilters)}
                             >
                                 <FontAwesomeIcon icon={faFilter} />
                             </Button>
                         </InputGroup>
                     </div>

                                         <div className="d-print-none filters-container">
                         {showSearchFilters && (
                             <div className="mb-3 equipment-filters-section">
                                 <Form.Label>קטגוריה</Form.Label>
                                 <Form.Select 
                                     className="category-select"
                                     value={selectedCategory} 
                                     onChange={(e) => setSelectedCategory(e.target.value)}
                                 >
                                     <option value="">כל הקטגוריות</option>
                                     {sortedCategories.map(cat => (
                                         <option key={cat} value={cat}>{cat}</option>
                                     ))}
                                     <option value="מתכלים">מתכלים</option>
                                     <option value="ציוד מושכר">ציוד מושכר</option>
                                 </Form.Select>
                             </div>
                         )}

                         {showSearchFilters && (
                             <div className="mb-3 equipment-filters-section">
                                 <Form.Label>תת-קטגוריה</Form.Label>
                                 <Form.Select 
                                     className="subcategory-select"
                                     value={selectedSubcategory} 
                                     onChange={(e) => setSelectedSubcategory(e.target.value)}
                                 >
                                     <option value="">כל התת-קטגוריות</option>
                                     {availableSubcategories.map(sub => (
                                         <option key={sub} value={sub}>{sub}</option>
                                     ))}
                                 </Form.Select>
                             </div>
                         )}

                         {showSearchFilters && (
                             <div className="d-flex gap-2 mb-3">
                                 <Button 
                                     className="clear-filters-button"
                                     variant="secondary" 
                                     onClick={clearFilters}
                                 >
                                     <FontAwesomeIcon icon={faTimes} /> נקה סינונים
                                 </Button>
                                 <Button 
                                     variant="outline-secondary" 
                                     onClick={() => setShowSearchFilters(false)}
                                 >
                                     <FontAwesomeIcon icon={faTimes} /> סגור סינונים
                                 </Button>
                             </div>
                         )}
                     </div>

                     {/* הודעת תוצאות החיפוש */}
                     <div className="d-print-none search-results-info">
                         {(searchTerm || selectedCategory || selectedSubcategory) && (
                             <Alert variant="info" className="mb-3 search-results-alert">
                                 <FontAwesomeIcon icon={faSearch} className="me-2" />
                                 תוצאות חיפוש: {filteredEventItems.length} פריטים נמצאו
                                 {searchTerm && <span className="ms-2">(חיפוש: "{searchTerm}")</span>}
                                 {selectedCategory && <span className="ms-2">(קטגוריה: {selectedCategory})</span>}
                                 {selectedSubcategory && <span className="ms-2">(תת-קטגוריה: {selectedSubcategory})</span>}
                             </Alert>
                         )}
                     </div>

                                          {filteredGroupedEventItems && Object.keys(filteredGroupedEventItems).length > 0 ? (
                         <Table striped bordered hover responsive size="sm" className="table-to-print equipment-table">
                             <thead>
                                 <tr>
                                     <th>פריט</th>
                                     <th>כמות</th>
                                     <th>קטגוריה</th>
                                     <th>תת-קטגוריה</th>
                                     <th>סטטוס הכנה</th>
                                     <th>סטטוס העמסה</th>
                                     {!isTechnician && <th>הערות ציוד</th>}
                                     <th>פרטי השכרה</th>
                                 </tr>
                             </thead>
                             <tbody>
                                 {Object.entries(filteredGroupedEventItems).map(([category, subcategories]) => (
                                     <React.Fragment key={category}>
                                         <tr className="table-dark equipment-category category-header">
                                            <td colSpan={isTechnician ? 7 : 8}><strong>{category}</strong></td>
                                         </tr>
                                         {Object.entries(subcategories).map(([subcategory, items]) => (
                                             <React.Fragment key={subcategory}>
                                                 <tr className="table-secondary equipment-subcategory subcategory-header">
                                                    <td colSpan={isTechnician ? 7 : 8} className="ps-4"><strong>{subcategory}</strong></td>
                                                 </tr>
                                                 {items.map(item => {
                                                     const isEquipment = 'equipment_name' in item;
                                                     const equipmentItem = item as EventEquipmentItem;
                                                     const isRented = isEquipment && equipmentItem.is_rented_item;
                                                     const isHeavy = isEquipment && !isRented
                                                         ? (allInventoryEquipment.find(eq => eq.id === equipmentItem.equipment)?.is_heavy)
                                                         : false;
                                                     
                                                     return (
                                                         <React.Fragment key={item.id}>
                                                             <tr 
                                                                 className={isRented ? 'table-info' : ''} 
                                                                 onClick={() => isEquipment && !isRented ? handleEquipmentRowClick(equipmentItem) : undefined}
                                                                 style={{ cursor: isEquipment && !isRented ? 'pointer' : 'default' }}
                                                                 title={isEquipment && !isRented ? 'לחץ לצפייה בפרטי הציוד' : undefined}
                                                             >
                                                                 <td>
                                                                     {isEquipment ? (equipmentItem.equipment_name || equipmentItem.rented_item_name) : (item as EventConsumableItem).consumable_name}
                                                                     {isHeavy ? <span className="text-danger ms-2" title="ציוד כבד">(כבד)</span> : null}
                                                                     {isRented && <span className="badge bg-info ms-2">מושכר</span>}
                                                                     {isEquipment && equipmentItem.last_modified_after_event && (
                                                                         <span className="text-warning ms-2" title={`נערך לאחר תאריך האירוע ב-${new Date(equipmentItem.last_modified_after_event).toLocaleDateString('he-IL')}`}>
                                                                             <FontAwesomeIcon icon={faExclamationTriangle} /> (נערך לאחר האירוע)
                                                                         </span>
                                                                     )}
                                                                 </td>
                                                                 <td>
                                                                     {isEquipment ? equipmentItem.quantity_needed : (item as EventConsumableItem).quantity_used}
                                                                     {isEquipment && !isRented && (() => {
                                                                       const inv = allInventoryEquipment.find(eq => eq.id === equipmentItem.equipment);
                                                                       const label = formatCasesLabel(equipmentItem.quantity_needed, inv?.case_quantity);
                                                                       return label ? <small className="d-block text-muted">({label})</small> : null;
                                                                     })()}
                                                                 </td>
                                                                 <td>{isEquipment ? (equipmentItem.category_name || equipmentItem.rented_item_category || '-') : 'מתכלים'}</td>
                                                                 <td>{isEquipment ? (equipmentItem.subcategory_name || equipmentItem.rented_item_subcategory || '-') : '-'}</td>
                                                                 <td>{isEquipment ? (equipmentItem.is_prepared ? 'הוכן' : 'לא הוכן') : ((item as EventConsumableItem).is_prepared ? 'הוכן' : 'לא הוכן')}</td>
                                                                 <td>{isEquipment ? (equipmentItem.is_loaded ? 'הועמס' : 'לא הועמס') : ((item as EventConsumableItem).is_loaded ? 'הועמס' : 'לא הועמס')}</td>
                                                                 {!isTechnician && (
                                                                    <td>
                                                                        {isEquipment ? (
                                                                            <>
                                                                                {equipmentItem.notes || '-'}
                                                                                {canViewNoteAuthor && equipmentItem.note_updated_by_name && (
                                                                                    <small className="d-block text-muted">נרשם על ידי: {equipmentItem.note_updated_by_name}</small>
                                                                                )}
                                                                            </>
                                                                        ) : '-'}
                                                                    </td>
                                                                 )}
                                                                 <td>
                                                                     {isRented ? (
                                                                         <div className="rented-item-details">
                                                                             <div><strong>ספק:</strong> {equipmentItem.rental_supplier_name || 'לא צוין'}</div>
                                                                             {equipmentItem.rented_arrival_date && (
                                                                                 <div><strong>תאריך הגעה:</strong> {new Date(equipmentItem.rented_arrival_date).toLocaleDateString('he-IL')}</div>
                                                                             )}
                                                                             {equipmentItem.rented_departure_date && (
                                                                                 <div><strong>תאריך עזיבה:</strong> {new Date(equipmentItem.rented_departure_date).toLocaleDateString('he-IL')}</div>
                                                                             )}
                                                                         </div>
                                                                     ) : (
                                                                         '-'
                                                                     )}
                                                                 </td>
                                                             </tr>
                                                         </React.Fragment>
                                                     )
                                                 })}
                                             </React.Fragment>
                                         ))}
                                     </React.Fragment>
                                 ))}
                             </tbody>
                         </Table>
                     ) : (
                         (searchTerm || selectedCategory || selectedSubcategory) ? (
                             <Alert variant="warning" className="d-print-none search-results-alert">
                                 <FontAwesomeIcon icon={faSearch} className="me-2" />
                                 לא נמצאו פריטים התואמים לחיפוש שלך. נסה לשנות את תנאי החיפוש.
                             </Alert>
                         ) : (
                             <Alert variant="info">אין ציוד משויך לאירוע זה.</Alert>
                         )
                     )}
                </Card.Body>
            </Card>
            
            <Card className="d-print-none">
                <Card.Header>קבצים מצורפים</Card.Header>
                <Card.Body>
                    {eventFiles.length > 0 ? (
                        <div className="table-responsive">
                            <table className="table table-striped">
                                <thead>
                                    <tr>
                                        <th>שם הקובץ</th>
                                        <th>הערות</th>
                                        <th>פעולות</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {eventFiles.map(file => (
                                        <tr key={file.id}>
                                            <td>{file.original_filename || file.description || `קובץ - ${file.id}`}</td>
                                            <td>{file.description || '-'}</td>
                                            <td>
                                                {hasAttachmentActions(file) && (
                                                    <div className="btn-group" role="group">
                                                      {isViewableFile(file) && (
                                                        <Button
                                                          variant="outline-primary"
                                                          size="sm"
                                                          onClick={() => handleFileView(file)}
                                                          className="me-1"
                                                        >
                                                          <FontAwesomeIcon icon={faEye} className="me-1" />
                                                          תצוגה
                                                        </Button>
                                                      )}
                                                      {!file.file_url?.trim() && (
                                                        <Button
                                                          variant="outline-success"
                                                          size="sm"
                                                          onClick={() => handleFileDownload(file)}
                                                        >
                                                          <FontAwesomeIcon icon={faDownload} className="me-1" />
                                                          הורדה
                                                        </Button>
                                                      )}
                                                    </div>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : <Alert variant="info">אין קבצים מצורפים לאירוע זה.</Alert>}
                </Card.Body>
            </Card>

            <div className="d-flex justify-content-end mt-4 d-print-none">
              <Button variant="secondary" onClick={() => onPrepareEvent(eventIdNumber!)} className="me-2" style={{ backgroundColor: '#8A2BE2', borderColor: '#8A2BE2' }} disabled={user?.role?.name === 'TECHNICIAN'} title={user?.role?.name === 'TECHNICIAN' ? 'אין לך הרשאה להכנת/העמסת אירועים' : ''}>
                <FontAwesomeIcon icon={faBoxOpen} className="me-1" />
                הכנה / העמסה
              </Button>
              <Button variant="outline-secondary" onClick={() => window.print()} className="print-event-button">
                <FontAwesomeIcon icon={faPrint} className="me-1" />
                 הדפס פרטי אירוע
              </Button>
            </div>

            <Modal show={showFileUploadModal} onHide={() => setShowFileUploadModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>העלאת קובץ/קישור לאירוע</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form onSubmit={handleFileUpload}>
                        <Form.Group className="mb-3">
                            <Form.Label>בחר קובץ (אופציונלי)</Form.Label>
                            <Form.Control type="file" onChange={handleFileChange} disabled={!!fileUrl} />
                            <Form.Text className="text-muted">
                                הגודל המקסימלי המותר הוא 20 מגה בייט
                                {selectedFileSize && (
                                    <span className={`ms-2 ${parseFloat(selectedFileSize) > 20 ? 'text-danger fw-bold' : 'text-success'}`}>
                                        (גודל קובץ נבחר: {selectedFileSize} מגה בייט)
                                    </span>
                                )}
                            </Form.Text>
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>או הזן קישור (אופציונלי)</Form.Label>
                            <Form.Control type="url" value={fileUrl} onChange={handleUrlChange} placeholder="https://example.com/document.pdf" disabled={!!selectedFile} />
                            <Form.Text className="text-muted">
                                אם ממלאים קישור — בשדה &quot;תיאור&quot; למטה חובה לכתוב הסבר קצר לקישור.
                            </Form.Text>
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>
                                תיאור
                                {fileUrl.trim() ? (
                                    <span className="text-danger" title="חובה כשמוסיפים קישור"> * (חובה לקישור)</span>
                                ) : (
                                    <span className="text-muted fw-normal"> (אופציונלי כשמעלים קובץ)</span>
                                )}
                            </Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={3}
                                value={fileDescription}
                                onChange={(e) => setFileDescription(e.target.value)}
                                required={!!fileUrl.trim()}
                                aria-required={!!fileUrl.trim()}
                            />
                            <Form.Text className={fileUrl.trim() ? 'text-danger' : 'text-muted'}>
                                {fileUrl.trim()
                                    ? 'חובה: יש למלא תיאור לפני שמירת הקישור.'
                                    : 'כשמעלים קובץ מהמחשב — אפשר להשאיר ללא תיאור. אם תבחרו קישור למעלה — יידרש תיאור.'}
                            </Form.Text>
                        </Form.Group>
                        <div className="d-flex justify-content-end">
                            <Button variant="secondary" onClick={() => setShowFileUploadModal(false)} className="me-2">
                                <FontAwesomeIcon icon={faTimes} className="me-1" />
                                ביטול
                            </Button>
                            <Button
                                variant="primary"
                                type="submit"
                                disabled={
                                    uploadingFile ||
                                    (!selectedFile && !fileUrl.trim()) ||
                                    (!!fileUrl.trim() && !fileDescription.trim())
                                }
                                title={
                                    fileUrl.trim() && !fileDescription.trim()
                                        ? 'יש למלא תיאור כשמוסיפים קישור'
                                        : undefined
                                }
                            >
                                {uploadingFile ? <Spinner animation="border" size="sm" /> : <><FontAwesomeIcon icon={faUpload} className="me-1" /> העלה קובץ/קישור</>}
                            </Button>
                        </div>
                    </Form>
                </Modal.Body>
            </Modal>

            <Modal show={showEventFormModal} onHide={handleEventFormCancel} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>עריכת פרטי אירוע</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {event && (
                        <EventForm
                            eventToEdit={event}
                            onSuccess={handleEventFormSuccess}
                            onCancel={handleEventFormCancel}
                        />
                    )}
                </Modal.Body>
            </Modal>

            {/* מודל בחירה דו-שלבי לסיום אירוע */}
            <EventEndChoiceModal
                show={showEventEndChoiceModal}
                onHide={() => setShowEventEndChoiceModal(false)}
                eventName={event?.event_name || ''}
                onEndWithoutDefects={handleEndWithoutDefects}
                onEndWithDefects={handleEndWithDefects}
            />

            {/* מודל דיווח תקלות מפורט */}
            <DefectReportModal
                show={showDefectReportModal}
                onHide={() => setShowDefectReportModal(false)}
                eventId={eventIdNumber}
                eventName={event?.event_name || ''}
                onDefectReport={handleDetailedDefectReport}
            />

            <Modal show={showDefectModal} onHide={() => setShowDefectModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>דיווח תקלה בסיום אירוע</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>האם יש ציוד תקול שברצונך לדווח עליו? מלא את הפרטים במידת הצורך. אם אין, השאר ריק.</p>
                    <Form onSubmit={handleDefectReportSubmit}>
                        <Form.Group className="mb-3" controlId="defectDescription">
                            <Form.Label>תיאור תקלה</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={3}
                                value={defectDescription}
                                onChange={(e) => setDefectDescription(e.target.value)}
                                placeholder="תיאור התקלה..."
                            />
                        </Form.Group>
                        <div className="d-flex justify-content-end">
                            <Button variant="secondary" onClick={() => { setShowDefectModal(false); setShowDefectReportModal(false); }} className="me-2">
                                <FontAwesomeIcon icon={faTimes} className="me-1" />
                                ביטול
                            </Button>
                            <Button variant="success" type="submit" disabled={isFinishingEvent}>
                                {isFinishingEvent ? <Spinner animation="border" size="sm" /> : <><FontAwesomeIcon icon={faCheck} className="me-1" /> סיים אירוע</>}
                            </Button>
                        </div>
                    </Form>
                </Modal.Body>
            </Modal>

            {/* Modal לבחירת ציוד בשכפול - הוסר כי האירוע תמיד מועתק עם כל הציוד */}

            {/* מודל הצעה לעריכת ציוד */}
            <Modal show={showEquipmentEditSuggestion} onHide={() => setShowEquipmentEditSuggestion(false)} size="lg" centered>
                <Modal.Header closeButton>
                    <Modal.Title>
                        <FontAwesomeIcon icon={faExclamationTriangle} className="me-2 text-warning" />
                        הצעה לעריכת רשימת הציוד
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Alert variant="info" className="mb-3">
                        <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
                        זוהה ציוד שנוסף או נערך לאחר תאריך האירוע. האם תרצה לערוך את רשימת הציוד?
                    </Alert>
                    
                    <div className="mb-3">
                        <h6>ציוד שזוהה כערוך לאחר תאריך האירוע:</h6>
                        <div className="list-group">
                            {equipmentNeedsReview.map((item, index) => (
                                <div key={index} className="list-group-item">
                                    <div className="d-flex justify-content-between align-items-center">
                                        <div>
                                            <strong>{item.equipment_name}</strong>
                                            <br />
                                            <small className="text-muted">
                                                כמות נדרשת: {item.quantity_needed}
                                            </small>
                                        </div>
                                        <div className="text-end">
                                            <small className="text-muted">
                                                נערך ב: {new Date(item.last_modified).toLocaleDateString('he-IL')}
                                                <br />
                                                תאריך סיום אירוע: {new Date(item.event_end_date).toLocaleDateString('he-IL')}
                                            </small>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                    
                    <div className="d-flex gap-2">
                        <Button 
                            variant="primary" 
                            onClick={() => {
                                setShowEquipmentEditSuggestion(false);
                                // פתיחת דף עריכת האירוע
                                navigate(`/events/edit/${eventIdNumber}`);
                            }}
                        >
                            <FontAwesomeIcon icon={faEdit} className="me-2" />
                            ערוך רשימת ציוד
                        </Button>
                        <Button 
                            variant="secondary" 
                            onClick={() => {
                                setShowEquipmentEditSuggestion(false);
                                showMessage('הצלחה', 'האירוע הסתיים בהצלחה! דיווח התקלות נשמר.', 'success');
                            }}
                        >
                            <FontAwesomeIcon icon={faCheck} className="me-2" />
                            המשך ללא עריכה
                        </Button>
                    </div>
                </Modal.Body>
            </Modal>

            {/* מודל הצעה לעריכת ציוד לאחר סיום אירוע */}
            <Modal show={showPostCompletionEditSuggestion} onHide={() => setShowPostCompletionEditSuggestion(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>
                        <FontAwesomeIcon icon={faInfoCircle} className="me-2 text-info" />
                        האם ברצונך לערוך את רשימת הציוד לאירוע?
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Alert variant="info" className="mb-3">
                        <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
                        האירוע הסתיים בהצלחה! הציוד הוחזר למלאי.
                        <br />
                        האם תרצה לערוך את רשימת הציוד לאירוע זה?
                    </Alert>
                    
                    <div className="d-flex gap-2 justify-content-center">
                        <Button 
                            variant="primary" 
                            onClick={() => {
                                setShowPostCompletionEditSuggestion(false);
                                // סגירת העתק ודפסה אם פתוחים
                                window.close();
                                // ניווט לעמוד ניהול הציוד
                                navigate(`/manage-event-equipment/${eventIdNumber}`);
                            }}
                        >
                            <FontAwesomeIcon icon={faEdit} className="me-2" />
                            ערוך רשימת ציוד
                        </Button>
                        <Button 
                            variant="secondary" 
                            onClick={() => {
                                setShowPostCompletionEditSuggestion(false);
                                const endedName = event?.event_name ? `"${event.event_name}"` : 'האירוע';
                                if (hadDefectsReported) {
                                    showMessage('הצלחה', `${endedName} הסתיים בהצלחה! דיווח התקלות נשמר.`, 'success');
                                } else {
                                    showMessage('הצלחה', `${endedName} הסתיים בהצלחה! הציוד הוחזר למלאי.`, 'success');
                                }
                                setHadDefectsReported(false);
                            }}
                        >
                            לא
                        </Button>
                    </div>
                </Modal.Body>
            </Modal>

            <MessageModal show={showMessageModal} onHide={() => setShowMessageModal(false)} title={messageModalTitle} message={messageModalContent} variant={messageModalVariant} />
            <ConfirmationModal show={showConfirmModal} onHide={() => setShowConfirmModal(false)} title={confirmModalTitle} message={confirmModalMessage} onConfirm={confirmAction || (() => {})} />
            
            {/* מודל פרטי ציוד */}
            <EquipmentDetailModal 
                show={showEquipmentDetailModal} 
                onHide={() => setShowEquipmentDetailModal(false)} 
                equipment={selectedEquipmentForDetails} 
                allEquipment={allInventoryEquipment} 
            />
        </Container>
    );
};

export default EventDetailsPage;
